import { RadioGroup as HeadlessRadioGroup } from "@headlessui/react";
import { ReactNode } from "react";
import { MouseEvent } from "react";
import { twMerge } from "tailwind-merge";

const Option = ({
  value,
  children,
  className,
  checkedClassName,
  radioClassName,
  checkedRadioClassName,
  onClick,
}: {
  value: string;
  children: ReactNode;
  className?: string;
  /** Extra classes applied to the option wrapper only when it is selected. */
  checkedClassName?: string;
  radioClassName?: string;
  /** Extra classes applied to the radio indicator only when it is selected. */
  checkedRadioClassName?: string;
  onClick?: (params: {
    checked: boolean;
    event: MouseEvent<HTMLDivElement>;
  }) => void;
}) => {
  return (
    <HeadlessRadioGroup.Option value={value}>
      {({ checked }) => (
        <div
          className={twMerge(
            "flex cursor-pointer items-center gap-2 p-3",
            checked && "bg-gray-200",
            checked && checkedClassName,
            className,
          )}
          onClick={(event) => onClick?.({ checked, event })}
        >
          <div
            className={twMerge(
              "flex h-4 w-4 flex-none items-center justify-center rounded-full border border-solid",
              checked ? "bg-zinc-700" : "bg-white",
              radioClassName,
              checked && checkedRadioClassName,
            )}
          >
            <div className={"h-1.5 w-1.5 rounded-full bg-white"} />
          </div>
          {children}
        </div>
      )}
    </HeadlessRadioGroup.Option>
  );
};

const Container = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={twMerge(
      "divide-y rounded-lg border border-solid border-zinc-300 [&>*:first-child>*]:rounded-t-lg [&>*:last-child>*]:rounded-b-lg",
      className,
    )}
  >
    {children}
  </div>
);

const RadioGroupRoot = ({
  children,
  onChange,
  value,
}: {
  children: ReactNode;
  onChange: (value: string) => void;
  value: string | undefined;
}) => {
  return (
    <HeadlessRadioGroup value={value} onChange={onChange}>
      {children}
    </HeadlessRadioGroup>
  );
};

export const RadioGroupLabel = HeadlessRadioGroup.Label;
export {
  RadioGroupRoot as RadioGroup,
  Option as RadioGroupOption,
  Container as RadioGroupContainer,
};
